/**
 * @class fsubmit
 * @description отправляет форму с помощью fetch
 * @param {object} options - параметры
 * @param {HTMLFormElement} options.form - форма, которую отправляем
 * @param {string} options.subject - тема письма
 * @param {function} options.onSuccess - функция-обработчик, вызываемая при успешной отправке
 * @param {function} options.onError - функция-обработчик, вызываемая при ошибке отправки
 * @param {string} options.url - URL, на который отправляется форма
 *
 * v.0.1.241214 - текущая версия
 * v.0.1.250206 - обновил код
 * v.0.1.250421 - добавил автоматический send_from
 * v.0.2.250428 - добавил beforeSend и this.result + доработки логики
 * v.0.2.250501 - отправка файлов + мелкие доработки
 * v.0.3.260214 - добавление полей вручную
 *
 */

class fsubmit {
  constructor(options) {
    const evt =
      typeof window !== 'undefined' && typeof window.event !== 'undefined'
        ? window.event
        : null
    if (evt) evt.preventDefault()

    options = options || {}

    this.form = options.form || (evt && evt.target ? evt.target : null)

    const formAction = this.form ? this.form.getAttribute('action') : ''
    this.url = (options.url ?? formAction ?? '') || location.href

    const formMethod = this.form ? this.form.getAttribute('method') : ''
    this.method = String(options.method ?? formMethod ?? 'POST').toUpperCase()

    this.headers = options.headers ?? { Accept: 'application/json' }

    // options.fields:
    // - null/undefined: собрать из формы
    // - object: добавить/переопределить поля поверх формы
    // - FormData: использовать как базу (и дополнить subject/send_from/user_info)
    // - string: отправить как есть (тело запроса)
    this.fields = options.fields ?? null

    this.sendingClass = options.sendingClass ?? '_sending'
    this.submitForm = options.submitForm ?? true
    this.debug = options.debug ?? false
    this.subject = options.subject ?? ''
    this.getUserInfo = options.getUserInfo ?? true
    this.result = options.result ?? null

    this.onBeforeSend = options.onBeforeSend ?? (() => Promise.resolve())
    this.onSuccess = options.onSuccess ?? (() => {})
    this.onError = options.onError ?? (() => {})
    this.onFinal = options.onFinal ?? (() => {})

    if (evt && evt.type === 'submit') this.handleSubmit()
    else this.bind()
  }

  bind() {
    if (!this.form) return
    this.form.addEventListener('submit', (e) => {
      e.preventDefault()
      this.handleSubmit()
    })
  }

  // сбор полей с формы
  getFields(extraFields = null) {
    const addon = extraFields === null ? this.fields : extraFields

    // 1) базовые данные
    let rawFD
    if (addon instanceof FormData) {
      rawFD = new FormData()
      for (let [k, v] of addon.entries()) rawFD.append(k, v)
    } else {
      rawFD = this.form ? new FormData(this.form) : new FormData()
    }

    // 2) subject
    if (this.subject) rawFD.set('subject', this.subject)

    // 3) send_from
    if (!rawFD.get('send_from')) rawFD.set('send_from', location.href)

    // 4) user_info
    if (
      this.getUserInfo &&
      typeof getUserInfo === 'function' &&
      typeof getUserInfo.getAll === 'function'
    ) {
      rawFD.set('user_info', JSON.stringify(getUserInfo.getAll()))
    }

    // 5) применяем доп.поля (object) поверх FormData
    if (addon && typeof addon === 'object' && !(addon instanceof FormData)) {
      Object.entries(addon).forEach(([k, v]) => {
        if (v === undefined || v === null) return

        // массивы поддерживаем как multi-value
        if (Array.isArray(v)) {
          rawFD.delete(k)
          v.forEach((item) => {
            if (item === undefined || item === null) return
            rawFD.append(k, item instanceof Blob ? item : String(item))
          })
          return
        }

        // File/Blob поддерживаем напрямую
        if (v instanceof Blob) {
          rawFD.set(k, v)
          return
        }

        rawFD.set(k, String(v))
      })
    }

    // 6) определяем, есть ли реальные файлы
    let hasFile = false
    for (let [, v] of rawFD.entries()) {
      if (v instanceof File && v.size) {
        hasFile = true
        break
      }
    }

    // 7) если есть файл - отправляем multipart
    if (hasFile) return rawFD

    // 8) иначе JSON (с поддержкой повторяющихся ключей как массивов)
    const obj = {}
    rawFD.forEach((v, k) => {
      if (v instanceof File) return
      const s = String(v).trim()
      if (!s) return

      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        if (Array.isArray(obj[k])) obj[k].push(s)
        else obj[k] = [obj[k], s]
      } else {
        obj[k] = s
      }
    })

    return JSON.stringify(obj)
  }

  showDebug(data = false, title = 'fsubmit debug') {
    if (data === false) data = this
    if (this.debug == true) console.log(title + ':\r\n', data)
  }

  async handleSubmit() {
    // 1) beforeSend
    try {
      await this.onBeforeSend(this.form)
    } catch (err) {
      console.error('✖ Ошибка в onBeforeSend:', err)
      this.onError(err)
      if (this.form) this.form.classList.remove(this.sendingClass)
      this.onFinal()
      return
    }

    // 2) собираем body
    let body
    try {
      if (typeof this.fields === 'string') {
        body = this.fields
      } else if (
        this.fields === null ||
        typeof this.fields === 'undefined' ||
        typeof this.fields === 'object'
      ) {
        body = this.getFields(this.fields)
      } else {
        throw new Error(
          'options.fields должен быть: object | string | FormData | null'
        )
      }
    } catch (err) {
      console.error('✖ Ошибка сборки полей:', err)
      this.onError(err)
      this.onFinal()
      return
    }

    const headers = { ...(this.headers || {}) }
    const opts = {
      method: this.method,
      headers,
      body
    }

    // Content-Type:
    // - для JSON выставляем
    // - для FormData не выставляем (браузер сам поставит boundary)
    if (this.isJSON(body)) headers['Content-Type'] = 'application/json'
    else delete headers['Content-Type']

    this.showDebug()

    // 3) отправляем
    if (this.submitForm === true) {
      try {
        this.result = null
        if (this.form) this.form.classList.add(this.sendingClass)

        const res = await fetch(this.url, opts)

        const accept = headers['Accept'] || headers['accept'] || ''
        let json

        if (accept === 'application/json') {
          json = await res.json()
        } else {
          const text = await res.text()
          try {
            json = JSON.parse(text)
          } catch {
            throw new Error('Ответ не JSON')
          }
        }

        if (json && json.success) {
          this.result = true
          if (this.form) this.form.reset()
          this.onSuccess(json)
        } else {
          this.result = false
          throw new Error(json && json.message ? json.message : 'send_error')
        }
      } catch (err) {
        this.result = false
        console.error('ERR fsubmit:', err)
        this.onError(err)
      } finally {
        this.showDebug()
        if (this.form) this.form.classList.remove(this.sendingClass)
        this.onFinal()
      }
    }
  }

  isJSON(variable) {
    if (typeof variable !== 'string') return false
    try {
      JSON.parse(variable)
      return true
    } catch (e) {
      return false
    }
  }
}
